<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RefundFailure } from '@/types/refunds'
import { useOrganizerRefund } from '@/composables/useOrganizerRefund'
import AppSpinner from '@/components/shared/AppSpinner.vue'

const props = defineProps<{
  open: boolean
  registrationId: number
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  confirmed: [refundFailure: RefundFailure | null]
}>()

const { t } = useI18n()

const {
  refundPreview,
  refundAmount,
  isLoading,
  submitting,
  error,
  refundFailure,
  hasChangedFromDefault,
  hasOnlinePayment,
  cancelBlockedReason,
  canRefundOnCancel,
  fetchPreview,
  confirm,
  reset,
} = useOrganizerRefund()

type Step = 'confirm' | 'refund'
const step = ref<Step>('confirm')

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      step.value = 'confirm'
      reset()
      void fetchPreview(props.registrationId)
    }
  },
)

function close(): void {
  if (submitting.value) return
  emit('update:open', false)
}

async function handleContinue(): Promise<void> {
  if (hasOnlinePayment.value) {
    step.value = 'refund'
  } else {
    await handleConfirm()
  }
}

async function handleConfirm(): Promise<void> {
  const success = await confirm(props.registrationId)
  if (!success) return

  const failure = refundFailure.value

  emit('update:open', false)
  emit('confirmed', failure)
  step.value = 'confirm'
  reset()
}

const alreadyRefundedTotal = computed<number>(() => {
  const preview = refundPreview.value
  if (preview === null) return 0
  return parseFloat(preview.refunded_amount) + parseFloat(preview.refund_pending_amount)
})

function formatCurrency(value: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(
    parseFloat(value),
  )
}
</script>

<template>
  <div
    v-if="open"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
    role="dialog"
    aria-modal="true"
    @click.self="close"
  >
    <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">

      <!-- Step 1: Confirmation -->
      <template v-if="step === 'confirm'">
        <h2 class="text-lg font-bold text-white mb-2">
          {{ t('participants.edit.cancel_confirm_title') }}
        </h2>
        <p class="text-sm text-slate-400 mb-6">
          {{ t('participants.edit.cancel_confirm_body') }}
        </p>

        <!-- Loading spinner while preview fetches -->
        <div v-if="isLoading" class="flex justify-center mb-4">
          <svg class="animate-spin h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>

        <div class="flex items-center gap-3 justify-end">
          <button
            type="button"
            :disabled="isLoading || submitting"
            class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
            @click="close"
          >
            {{ t('common.close') }}
          </button>
          <button
            type="button"
            :disabled="isLoading || submitting"
            class="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-2"
            @click="handleContinue"
          >
            <AppSpinner v-if="submitting" size="button" />
            <!-- When preview is loaded: show Continue (has Stripe) or Confirm (no Stripe) -->
            <!-- When preview is still loading: show Continue as a neutral label -->
            <template v-if="!isLoading && hasOnlinePayment">
              {{ t('common.next') }}
            </template>
            <template v-else>
              {{ t('common.confirm') }}
            </template>
          </button>
        </div>
      </template>

      <!-- Step 2: Refund amount input -->
      <template v-else-if="step === 'refund'">
        <h2 class="text-lg font-bold text-white mb-4">{{ t('participants.refund.title') }}</h2>

        <!-- Loading spinner -->
        <div v-if="isLoading" class="flex justify-center mb-4">
          <svg class="animate-spin h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>

        <template v-else-if="refundPreview">
          <!-- Already refunded / being processed -->
          <p
            v-if="alreadyRefundedTotal > 0"
            class="text-sm text-slate-400 mb-4"
            data-testid="cancel-already-refunded"
          >
            {{ t('participants.refund.already_refunded', { amount: formatCurrency(String(alreadyRefundedTotal)) }) }}
          </p>

          <!-- Blocked: the registration is cancelled without a refund -->
          <div
            v-if="cancelBlockedReason !== null"
            class="mb-4 rounded-lg bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-300"
            role="status"
            data-testid="cancel-refund-blocked"
          >
            {{ t(`participants.refunds.cancel_without_refund.${cancelBlockedReason}`) }}
          </div>

          <!-- Refundable total helper text -->
          <p v-if="canRefundOnCancel" class="text-sm text-slate-400 mb-4">
            {{ t('participants.refund.refundable_total', { amount: formatCurrency(refundPreview.refundable_total) }) }}
          </p>

          <!-- Refund amount input -->
          <div v-if="canRefundOnCancel" class="mb-4">
            <label class="block text-sm font-medium text-slate-300 mb-1">
              {{ t('participants.refund.amount_label') }}
            </label>
            <input
              v-model.number="refundAmount"
              type="number"
              step="0.01"
              min="0"
              :max="parseFloat(refundPreview.max_refund_amount)"
              class="w-full rounded-lg bg-slate-900 border border-slate-600 text-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              :class="{ 'border-red-500': refundAmount < 0 || refundAmount > parseFloat(refundPreview.max_refund_amount) }"
            />

            <!-- Policy default note (shown when organiser changed the value) -->
            <p v-if="hasChangedFromDefault" class="mt-1 text-xs text-slate-400">
              {{ t('participants.refund.default_from_policy', { amount: formatCurrency(refundPreview.policy_refund_amount) }) }}
            </p>

            <!-- Inline validation error for refund_amount -->
            <p
              v-if="error === 'participants.refund.error_amount_invalid'"
              class="mt-1 text-sm text-red-400"
            >
              {{ t(error) }}
            </p>
          </div>

          <!-- Genuine errors (403, generic). Always local i18n keys. -->
          <div
            v-if="error && error !== 'participants.refund.error_amount_invalid'"
            class="mb-4 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
          >
            {{ t(error) }}
          </div>
        </template>

        <div class="flex items-center gap-3 justify-end">
          <button
            type="button"
            :disabled="submitting"
            class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
            @click="step = 'confirm'"
          >
            {{ t('common.back') }}
          </button>
          <button
            type="button"
            :disabled="
              submitting ||
              isLoading ||
              refundPreview === null ||
              (canRefundOnCancel &&
                (refundAmount < 0 ||
                  refundAmount > parseFloat(refundPreview?.max_refund_amount ?? '0')))
            "
            class="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-2"
            @click="handleConfirm"
          >
            <AppSpinner v-if="submitting" size="button" />
            {{ t('common.confirm') }}
          </button>
        </div>
      </template>

    </div>
  </div>
</template>
